/**
 * The five lever cards — the right rail's causes. Each carries its number and name, the big
 * result number with a plain unit caption, its control, a one-line summary of what the current
 * setting selects, and a link to the full table behind it (`drawer.tsx`, opened over the
 * centre). Editing here is the ONLY way the draft moves (build handover §3).
 */

import type { Dispatch, ReactNode } from "react";

import type { Scenario5LeverId, ScenarioContext, ScenarioParams, ScenarioResult } from "@/lib/data";
import { cn } from "@/lib/utils";

import { LEVER_ICON } from "./icons";
import { FLOOR_ENVELOPE_M2, SYSTEMS } from "./mock";
import type { ScenariosAction } from "./state";

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");

interface LeverCardsProps {
  draft: ScenarioParams;
  result: ScenarioResult;
  ctx: ScenarioContext;
  dispatch: Dispatch<ScenariosAction>;
  onDrawer: (lever: Scenario5LeverId) => void;
  onHot: (lever: Scenario5LeverId | null) => void;
}

function CardShell({
  id,
  num,
  title,
  commercial,
  agentSet,
  deltaText,
  deltaPositive,
  unitLabel,
  on,
  children,
  note,
  onDrawer,
}: {
  id: Scenario5LeverId;
  num: number;
  title: string;
  commercial?: boolean;
  agentSet: boolean;
  deltaText: string;
  deltaPositive: boolean;
  unitLabel: string;
  on: boolean;
  children: ReactNode;
  note: ReactNode;
  onDrawer: () => void;
}) {
  return (
    <div className={cn("card", on && "on", commercial && "commercial")} data-spot={`lever-${id}`}>
      <div className="chead">
        <div className="cl">
          <div className="cnum">
            Lever {num}
            {commercial ? " · Commercial" : ""}
            {agentSet ? <span className="agset"> · Agent set</span> : null}
          </div>
          <div className="ct">
            <span className="lever-ic">{LEVER_ICON[id]}</span> {title}
          </div>
        </div>
        <div className="cr">
          <div className={cn("delta", deltaText === "·" ? "zero" : deltaPositive ? "pos" : null)}>
            {deltaText}
          </div>
          <div className="cru">{unitLabel}</div>
        </div>
      </div>
      <div className="cctrl">{children}</div>
      <div className="cdet">
        <div className="cdet-row">
          <div className="mini-note">{note}</div>
          <button className="xlink" onClick={onDrawer}>
            table
          </button>
        </div>
      </div>
    </div>
  );
}

function Slider({
  min,
  max,
  value,
  disabled,
  onChange,
  onCommit,
}: {
  min: number;
  max: number;
  value: number;
  disabled?: boolean;
  onChange: (v: number) => void;
  onCommit?: () => void;
}) {
  const pct = disabled ? 0 : ((value - min) / (max - min)) * 100;
  return (
    <div className={cn("pslider", disabled && "off")}>
      <div className="ptrack">
        <i style={{ width: `${pct.toFixed(1)}%` }} />
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={1}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.currentTarget.value))}
        onPointerUp={onCommit}
        onKeyUp={onCommit}
      />
    </div>
  );
}

export function LeverCards({ draft, result, ctx, dispatch, onDrawer, onHot }: LeverCardsProps) {
  const relo = draft.reloThreshold;
  const relDelta = relo < 0 ? "·" : `− ${fmt(result.relocate)}`;
  const packDelta = draft.packCount === 0 ? "·" : `− ${fmt(result.pack)}`;
  const convDelta = draft.convCount === 0 ? "·" : `− ${fmt(result.conv)}`;
  const capDelta = result.addFaces > 0 ? `+ ${fmt(result.addFaces)}` : "·";
  const paceFig = deferInfo(draft.pacePct, ctx);
  const paceDelta = draft.pacePct === 0 ? "·" : `− ${fmt(paceFig.defer)}`;

  const hot = (id: Scenario5LeverId) => {
    onHot(id);
    window.setTimeout(() => onHot(null), 1600);
  };

  return (
    <div className="cards">
      <CardShell
        id="relo"
        num={1}
        title="Relocate non-movers"
        agentSet={draft.agentSet.includes("relo")}
        deltaText={relDelta}
        deltaPositive={false}
        unitLabel="faces released"
        on={result.relocate > 0}
        note={
          <>
            {relo < 0 ? (
              "Off. Nothing selected."
            ) : (
              <>
                {relo === 0 ? (
                  "Dead stock only"
                ) : (
                  <>
                    Up to <b>{relo}</b> picks a year
                  </>
                )}{" "}
                · <b>{fmt(result.relocate)}</b> faces
                {result.bBandWarning ? <span className="red-t"> · reaches into B</span> : null}
              </>
            )}
          </>
        }
        onDrawer={() => onDrawer("relo")}
      >
        <Slider
          min={-1}
          max={24}
          value={relo}
          onChange={(v) => {
            dispatch({ type: "relo", threshold: v });
            hot("relo");
          }}
        />
        <div className="param-ctrl-row">
          <span>Off</span>
          <b>{relo < 0 ? "Off" : relo === 0 ? "dead stock only" : `≤ ${relo} picks/yr`}</b>
          <span>24</span>
        </div>
      </CardShell>

      <CardShell
        id="pack"
        num={2}
        title="Pack rationalize"
        agentSet={draft.agentSet.includes("pack")}
        deltaText={packDelta}
        deltaPositive={false}
        unitLabel="faces released"
        on={result.pack > 0}
        note={
          <>
            {draft.packCount === 0 ? (
              "No families selected."
            ) : (
              <>
                <b>{draft.packCount}</b> of 10 families · <b>{fmt(result.pack)}</b> faces
                {result.waiverCount > 0 ? <span className="warn-t"> · 2 waivers</span> : null}
              </>
            )}
          </>
        }
        onDrawer={() => onDrawer("pack")}
      >
        <Slider
          min={0}
          max={10}
          value={draft.packCount}
          onChange={(v) => {
            dispatch({ type: "pack", count: v });
            hot("pack");
          }}
        />
        <div className="param-ctrl-row">
          <span>0</span>
          <b>{draft.packCount} of 10 families</b>
          <span>10</span>
        </div>
      </CardShell>

      <CardShell
        id="conv"
        num={3}
        title="Rx to Dx convert"
        agentSet={draft.agentSet.includes("conv")}
        deltaText={convDelta}
        deltaPositive={false}
        unitLabel="faces released"
        on={result.conv > 0}
        note={
          <>
            {draft.convCount === 0 ? (
              "No sets selected."
            ) : (
              <>
                <b>{draft.convCount}</b> of 8 sets · <b>{fmt(result.conv)}</b> faces
                {result.uncertifiedSetsEngaged ? (
                  <span className="warn-t"> · MDR check</span>
                ) : null}
              </>
            )}
          </>
        }
        onDrawer={() => onDrawer("conv")}
      >
        <Slider
          min={0}
          max={8}
          value={draft.convCount}
          onChange={(v) => {
            dispatch({ type: "conv", count: v });
            hot("conv");
          }}
        />
        <div className="param-ctrl-row">
          <span>0</span>
          <b>{draft.convCount} of 8 sets</b>
          <span>8</span>
        </div>
      </CardShell>

      <CardShell
        id="cap"
        num={4}
        title="Add pick-face capacity"
        agentSet={draft.agentSet.includes("cap")}
        deltaText={capDelta}
        deltaPositive={result.addFaces > 0}
        unitLabel="faces added"
        on={result.addFaces > 0}
        note={
          result.addFaces > 0 && draft.capSystem ? (
            <>
              <b>
                {Math.round(
                  FLOOR_ENVELOPE_M2 -
                    draft.capUnits * (SYSTEMS.find((s) => s.key === draft.capSystem)?.addM2 ?? 0),
                )}
              </b>{" "}
              of {fmt(FLOOR_ENVELOPE_M2)} m² free ·{" "}
              <b>
                £
                {(
                  draft.capUnits * (SYSTEMS.find((s) => s.key === draft.capSystem)?.addCostM ?? 0)
                ).toFixed(1)}
                m
              </b>{" "}
              · {SYSTEMS.find((s) => s.key === draft.capSystem)?.addLeadMonths} mo
            </>
          ) : (
            <>
              Nothing added. Ceiling stays at <b>{fmt(ctx.model.capd.ceil)}</b>.
            </>
          )
        }
        onDrawer={() => onDrawer("cap")}
      >
        <div className="sys-row">
          <button
            className={cn("sys-btn none", !draft.capSystem && "active")}
            onClick={() => {
              dispatch({ type: "capSystem", system: null });
              hot("cap");
            }}
          >
            None
          </button>
          {SYSTEMS.map((s) => (
            <button
              key={s.key}
              className={cn("sys-btn", draft.capSystem === s.key && "active")}
              onClick={() => {
                dispatch({ type: "capSystem", system: s.key });
                hot("cap");
              }}
            >
              {s.name}
            </button>
          ))}
        </div>
        <Slider
          min={0}
          max={6}
          value={draft.capUnits}
          disabled={!draft.capSystem}
          onChange={(v) => {
            dispatch({ type: "capUnits", units: v });
            hot("cap");
          }}
        />
        <div className="param-ctrl-row">
          <span>0</span>
          <b>
            {draft.capSystem
              ? `${draft.capUnits} unit${draft.capUnits === 1 ? "" : "s"}`
              : "pick a system first"}
          </b>
          <span>6</span>
        </div>
      </CardShell>

      <CardShell
        id="pace"
        num={5}
        title="Pace the NPI intake"
        commercial
        agentSet={draft.agentSet.includes("pace")}
        deltaText={paceDelta}
        deltaPositive={false}
        unitLabel="slots moved"
        on={draft.pacePct > 0}
        note={
          paceFig.defer > 0 ? (
            <>
              <b>{fmt(paceFig.defer)}</b> out of {paceFig.pinch} ·{" "}
              <span className="warn-t">Commercial decides</span>
            </>
          ) : (
            <>Full intake still lands in {paceFig.pinch}.</>
          )
        }
        onDrawer={() => onDrawer("pace")}
      >
        <Slider
          min={0}
          max={100}
          value={draft.pacePct}
          onChange={(v) => {
            dispatch({ type: "pace", pct: v });
            hot("pace");
          }}
        />
        <div className="param-ctrl-row">
          <span>0%</span>
          <b>{draft.pacePct}% deferred</b>
          <span>100%</span>
        </div>
      </CardShell>
    </div>
  );
}

function deferInfo(pacePct: number, ctx: ScenarioContext) {
  const pinch = ctx.model.years?.[1] ?? 2027;
  const families = ctx.model.npiFamilies.filter((f) => f.rel === pinch);
  const total = families.reduce((a, f) => a + f.total, 0);
  const defer = Math.round(total * (Math.max(0, Math.min(100, pacePct)) / 100));
  return { pinch, defer };
}
